import { useEffect, useState } from "react";
import { Check, Copy, Loader2, RefreshCw, Trash2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import {
  useCustomHostnameStatus,
  useDeleteCustomHostname,
  useRegisterCustomHostname,
  useRevalidateCustomHostname,
  type CustomHostnameStatus,
} from "../lib/project-wizard";
import { api } from "../lib/api";
import {
  resolveCustomHostnameE2eVerdict,
  type ServerProbeResult,
} from "../lib/custom-hostname-e2e-verdict";
import { ConfirmDialog } from "./ConfirmDialog";

interface Props {
  projectId: number;
  /** If we already know a hostname is set (from project detail), pre-poll. */
  hasRegistered: boolean;
}

/**
 * Customer-facing CNAME target. Customers point their hostname (e.g. t.kunde.de)
 * here; Cloudflare for SaaS picks up the request via Host-Header and routes
 * internally to the configured Fallback Origin (custom.dhc-track.io).
 *
 * Apex is short and memorable; CF apex is proxied so traffic lands at our zone.
 */
const CUSTOMER_CNAME_TARGET = "dhc-track.io";

/**
 * Customer's DNS panel uses the apex (last two labels) as the zone they manage,
 * and the sub-portion is what they actually type into the "Host"/"Name" field.
 *   tagging.digitalhoneycomb.de  → { sub: "tagging", apex: "digitalhoneycomb.de" }
 *   _cf-custom-hostname.tagging.digitalhoneycomb.de
 *                                → { sub: "_cf-custom-hostname.tagging", apex: "digitalhoneycomb.de" }
 *
 * Heuristic: last 2 labels = apex. Works for *.de/.com/.io etc. Edge cases
 * like *.co.uk fall back to showing the full FQDN as sub.
 */
function splitHostByApex(
  host: string,
  customerHostname: string,
): { sub: string; apex: string } {
  const labels = customerHostname.split(".");
  const apex =
    labels.length >= 2 ? labels.slice(-2).join(".") : customerHostname;
  if (host === apex) return { sub: "@", apex };
  if (host.endsWith("." + apex)) {
    return { sub: host.slice(0, -(apex.length + 1)), apex };
  }
  return { sub: host, apex: "" };
}

function copy(text: string, label: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} kopiert`),
    () => toast.error("Kopieren fehlgeschlagen"),
  );
}

function isActive(status: CustomHostnameStatus | undefined): boolean {
  if (!status?.registered) return false;
  return (
    status.cfStatus === "active" &&
    status.sslStatus === "active" &&
    status.workerRouteActive
  );
}

type E2eState = "idle" | "checking" | "ok" | "fail";

/**
 * End-to-end probe: hit https://<hostname>/health and verify the response.
 * This walks the full chain Browser → CF → Worker → Railway → api.
 */
async function probeE2E(hostname: string): Promise<{
  state: "ok" | "fail";
  detail: string;
}> {
  try {
    const res = await fetch(`https://${hostname}/health`, {
      method: "GET",
      mode: "cors",
      cache: "no-store",
    });
    if (!res.ok) {
      return { state: "fail", detail: `HTTP ${res.status} ${res.statusText}` };
    }
    const body = await res.text();
    if (body.toLowerCase().includes("ok")) {
      return { state: "ok", detail: `${res.status} OK` };
    }
    return { state: "ok", detail: `HTTP ${res.status}` };
  } catch (err) {
    return {
      state: "fail",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Server-side leg of the E2E check (TASK-116): asks our own API to probe
 * the hostname, independent of the operator's browser/DNS cache. Never
 * throws — a failed lookup is a ServerProbeResult, same contract as
 * probeE2E above.
 */
async function probeServerE2e(projectId: number): Promise<ServerProbeResult> {
  try {
    const res = await api<{ ok: boolean; status?: number; detail: string }>(
      `/admin/projects/${projectId}/custom-hostname/e2e`,
    );
    return { ok: res.ok, detail: res.detail };
  } catch (err) {
    return {
      ok: false,
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

export function CustomHostnameManager({ projectId, hasRegistered }: Props) {
  const [input, setInput] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [verifyEpoch, setVerifyEpoch] = useState(0);

  // Poll while registered but not yet fully active.
  const status = useCustomHostnameStatus(
    hasRegistered ? projectId : null,
    hasRegistered ? 5_000 : false,
  );
  const register = useRegisterCustomHostname(projectId);
  const remove = useDeleteCustomHostname(projectId);
  const revalidate = useRevalidateCustomHostname(projectId);

  const active = isActive(status.data);

  // Stop polling once active
  const effectivePolling = hasRegistered && status.data && !active;
  const _ = effectivePolling; // (refetchInterval is decided at hook call; no-op marker)
  void _;

  if (!hasRegistered) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-text-secondary">
          Falls der Kunde eine eigene Subdomain wie <code>t.kunde.de</code>{" "}
          nutzen will. Kunde muss CNAME auf <code>{CUSTOMER_CNAME_TARGET}</code>{" "}
          setzen.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) =>
              setInput(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))
            }
            placeholder="t.kunde.de"
            className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
          />
          <button
            type="button"
            disabled={!input || register.isPending}
            onClick={() =>
              register.mutate(input, {
                onSuccess: () =>
                  toast.success("Hostname registriert, Validierung läuft"),
                onError: (err) =>
                  toast.error(
                    `Registrierung fehlgeschlagen: ${(err as Error).message}`,
                  ),
              })
            }
            className="inline-flex items-center gap-1 rounded-md bg-accent px-3 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
          >
            {register.isPending && (
              <Loader2 size={14} className="animate-spin" />
            )}
            Registrieren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border-primary bg-bg-secondary p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-mono text-sm text-text-primary">
            {status.data?.hostname ?? "lade…"}
          </p>
          {status.isFetching && (
            <RefreshCw size={11} className="animate-spin text-text-tertiary" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setVerifyEpoch((n) => n + 1);
              toast.message("Cloudflare prüft neu…");
              // A plain refetch would just re-read Cloudflare's frozen verdict.
              revalidate.mutate(undefined, {
                onSuccess: (res) => {
                  if (res.revalidateError) {
                    toast.error(`Neuprüfung fehlgeschlagen: ${res.revalidateError}`);
                  }
                },
                onError: (err) =>
                  toast.error(
                    `Neuprüfung fehlgeschlagen: ${
                      err instanceof Error ? err.message : String(err)
                    }`,
                  ),
                // No refetch here — the hook invalidates the status query.
              });
            }}
            disabled={status.isFetching || revalidate.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs font-medium text-text-secondary hover:bg-bg-hover hover:text-text-primary disabled:opacity-50"
          >
            <RefreshCw
              size={11}
              className={
                status.isFetching || revalidate.isPending ? "animate-spin" : ""
              }
            />
            Verify
          </button>
          {status.data?.hostname && (
            <button
              type="button"
              onClick={() => copy(status.data!.hostname!, "Hostname")}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-bg-hover hover:text-text-primary"
              aria-label="Hostname kopieren"
            >
              <Copy size={12} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmRemove(true)}
            disabled={remove.isPending}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-danger-bg hover:text-danger disabled:opacity-50"
            aria-label="Custom Hostname entfernen"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <StatusCell
          label="CF-Status"
          value={status.data?.cfStatus ?? "—"}
          ok={status.data?.cfStatus === "active"}
        />
        <StatusCell
          label="SSL"
          value={status.data?.sslStatus ?? "—"}
          ok={status.data?.sslStatus === "active"}
        />
        <StatusCell
          label="Worker-Route"
          value={status.data?.workerRouteActive ? "active" : "missing"}
          ok={status.data?.workerRouteActive === true}
        />
      </div>

      {/* Cloudflare says WHY it hasn't verified — showing only "pending" left
          the operator guessing for 16 hours once (TASK-40). */}
      {(status.data?.verificationErrors?.length ?? 0) > 0 && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/10 p-2">
          <p className="text-xs font-medium text-text-primary">
            Cloudflare meldet:
          </p>
          <ul className="mt-1 space-y-0.5">
            {status.data!.verificationErrors!.map((e) => (
              <li key={e} className="font-mono text-xs text-text-secondary">
                {e}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-text-tertiary">
            Stehen die DNS-Einträge unten alle auf „gesetzt", ist das meist ein
            veraltetes Urteil von Cloudflare — Verify drücken stößt eine
            Neuprüfung an.
          </p>
        </div>
      )}

      {active ? (
        <p className="mt-3 inline-flex items-center gap-1 text-xs text-success">
          <Check size={12} /> Aktiv — alle Komponenten laufen.
        </p>
      ) : (
        <ValidationHints status={status.data} verifyEpoch={verifyEpoch} />
      )}

      {status.data?.hostname && (
        <E2eProbe
          projectId={projectId}
          hostname={status.data.hostname}
          verifyEpoch={verifyEpoch}
        />
      )}

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Custom Hostname entfernen?"
        variant="danger"
        confirmLabel="Entfernen"
        description={
          <>
            <strong>{status.data?.hostname}</strong> wird aus Cloudflare
            deregistriert. Tracking auf dieser URL stoppt sofort. Die Subdomain
            <em> {".dhc-track.io"}</em> bleibt unverändert.
          </>
        }
        onConfirm={() => {
          remove.mutate(undefined, {
            onSuccess: () => toast.success("Hostname entfernt"),
            onError: (err) =>
              toast.error(`Entfernen fehlgeschlagen: ${(err as Error).message}`),
          });
          setConfirmRemove(false);
        }}
      />
    </div>
  );
}

/**
 * Resolve a DNS record via Cloudflare DoH (1.1.1.1). Browser-CORS-friendly.
 * Returns the array of answer values (data field) or null on error.
 * For CNAME: data is the target (often with trailing dot).
 * For TXT: data is the quoted string (we strip quotes).
 */
async function dohResolve(
  name: string,
  type: "CNAME" | "TXT",
): Promise<string[] | null> {
  try {
    const url = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(name)}&type=${type}`;
    const res = await fetch(url, {
      headers: { Accept: "application/dns-json" },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { Answer?: { type: number; data: string }[] };
    if (!json.Answer) return [];
    const wanted = type === "CNAME" ? 5 : 16; // RFC: CNAME=5, TXT=16
    return json.Answer.filter((a) => a.type === wanted).map((a) =>
      type === "TXT"
        ? a.data.replace(/^"|"$/g, "").replace(/" "/g, "")
        : a.data.replace(/\.$/, ""),
    );
  } catch {
    return null;
  }
}

type RecordVerify = "loading" | "ok" | "missing" | "wrong";

interface RecordSpec {
  key: string;
  type: "CNAME" | "TXT";
  host: string;
  value: string;
  note?: string;
  /** When true, mark verified iff sslStatus === "active" (CF auth, not DNS). */
  bindsToSsl?: boolean;
}

function ValidationHints({
  status,
  verifyEpoch,
}: {
  status: CustomHostnameStatus | undefined;
  /** Increments on Verify-button to refire DoH lookups. */
  verifyEpoch: number;
}) {
  const [verify, setVerify] = useState<Record<string, RecordVerify>>({});

  const records: RecordSpec[] = [];
  if (status?.hostname) {
    records.push({
      key: "cname-traffic",
      type: "CNAME",
      host: status.hostname,
      value: CUSTOMER_CNAME_TARGET,
      note: "Traffic-Routing — ohne diesen Record kommt nichts beim Tracker an",
    });
    if (status.ownershipVerification) {
      records.push({
        key: "txt-ownership",
        type: "TXT",
        host: status.ownershipVerification.name,
        value: status.ownershipVerification.value,
        note: "Cloudflare Ownership-Verification",
      });
    }
    for (const r of status.validationRecords) {
      if (r.txt_name && r.txt_value) {
        records.push({
          key: `txt-ssl-${r.txt_name}`,
          type: "TXT",
          host: r.txt_name,
          value: r.txt_value,
          note: "SSL-Validierung",
          bindsToSsl: true,
        });
      }
    }
  }

  // Run DoH checks for each record. Re-run when status changes or verify clicked.
  useEffect(() => {
    if (records.length === 0) return;
    let cancelled = false;
    setVerify((s) => {
      const next: Record<string, RecordVerify> = { ...s };
      for (const r of records) next[r.key] = "loading";
      return next;
    });
    Promise.all(
      records.map(async (r) => {
        const answers = await dohResolve(r.host, r.type);
        if (cancelled) return;
        let result: RecordVerify;
        if (answers === null) result = "missing";
        else if (answers.length === 0) result = "missing";
        else if (answers.some((a) => a === r.value)) result = "ok";
        else result = "wrong";
        setVerify((s) => ({ ...s, [r.key]: result }));
      }),
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status?.hostname,
    status?.ownershipVerification?.value,
    status?.validationRecords?.length,
    verifyEpoch,
  ]);

  if (records.length === 0) {
    return (
      <p className="mt-3 text-xs text-text-secondary">
        Status wird automatisch geprüft. Ohne pending-Records ist alles ok —
        falls trotzdem nicht active, drücke Verify.
      </p>
    );
  }

  // Overall progress summary
  const dnsRecords = records.filter((r) => !r.bindsToSsl);
  const sslRecords = records.filter((r) => r.bindsToSsl);
  const dnsAllOk = dnsRecords.every((r) => verify[r.key] === "ok");
  const sslIssued = status?.sslStatus === "active";

  return (
    <div className="mt-3 space-y-3 text-xs">
      <p className="text-text-secondary">
        Diese DNS-Records muss der Kunde setzen, dann läuft die Validierung
        automatisch (Polling alle 5s):
      </p>

      {dnsAllOk && !sslIssued && sslRecords.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-info bg-info-bg/40 px-3 py-2">
          <Loader2 size={12} className="mt-0.5 shrink-0 animate-spin text-info" />
          <p className="text-text-primary">
            <strong>DNS sitzt.</strong> Cloudflare stellt jetzt das SSL-Cert
            aus — typisch 1–5 Min. Status oben aktualisiert sich automatisch.
          </p>
        </div>
      )}
      {dnsAllOk && sslIssued && (
        <div className="flex items-start gap-2 rounded-md border border-success bg-success-bg/40 px-3 py-2">
          <Check size={12} className="mt-0.5 shrink-0 text-success" />
          <p className="text-text-primary">
            <strong>Alles drin.</strong> DNS gesetzt + SSL-Cert ausgestellt.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {records.map((r) => (
          <DnsRow
            key={r.key}
            spec={r}
            customerHostname={status!.hostname!}
            verify={verify[r.key] ?? "loading"}
          />
        ))}
      </div>
    </div>
  );
}

function VerifyBadge({ state }: { state: RecordVerify }) {
  if (state === "loading")
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border-primary bg-bg-secondary px-2 py-0.5 font-mono text-[10px] text-text-tertiary">
        <Loader2 size={9} className="animate-spin" />
        prüfe…
      </span>
    );
  if (state === "ok")
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-success bg-success-bg px-2 py-0.5 font-mono text-[10px] font-medium text-success">
        <Check size={9} />
        gesetzt
      </span>
    );
  if (state === "wrong")
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-danger bg-danger-bg px-2 py-0.5 font-mono text-[10px] font-medium text-danger">
        <XIcon size={9} />
        falscher Wert
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-warning bg-warning-bg px-2 py-0.5 font-mono text-[10px] font-medium text-warning">
      <XIcon size={9} />
      fehlt
    </span>
  );
}

function DnsRow({
  spec,
  customerHostname,
  verify,
}: {
  spec: RecordSpec;
  customerHostname: string;
  verify: RecordVerify;
}) {
  const { type, host, value, note } = spec;
  const { sub, apex } = splitHostByApex(host, customerHostname);
  return (
    <div
      className={`rounded-md border bg-bg-tertiary p-2 ${
        verify === "ok"
          ? "border-success/40"
          : verify === "wrong" || verify === "missing"
            ? "border-warning/40"
            : "border-border-primary"
      }`}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-medium text-text-primary">
          {type}
        </span>
        <VerifyBadge state={verify} />
      </div>
      <div className="grid grid-cols-[1fr_1fr] items-center gap-2 font-mono">
        <div className="flex items-center gap-1 break-all">
          <span className="text-text-primary">{sub}</span>
          <button
            type="button"
            onClick={() => copy(sub, "Host")}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-text-secondary hover:bg-bg-hover hover:text-text-primary"
            aria-label="Host (Sub) kopieren"
          >
            <Copy size={10} />
          </button>
          {apex && <span className="text-text-tertiary">.{apex}</span>}
        </div>
        <div className="flex items-start gap-1 break-all">
          <span className="flex-1 text-text-primary">{value}</span>
          <button
            type="button"
            onClick={() => copy(value, `${type}-Wert`)}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-text-secondary hover:bg-bg-hover hover:text-text-primary"
            aria-label={`${type}-Wert kopieren`}
          >
            <Copy size={10} />
          </button>
        </div>
      </div>
      {note && <p className="mt-1 text-[10px] text-text-tertiary">{note}</p>}
    </div>
  );
}

function StatusCell({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 ${
        ok
          ? "border-success/40 bg-success-bg/30"
          : "border-border-primary bg-bg-tertiary"
      }`}
    >
      {ok ? (
        <Check size={12} className="shrink-0 text-success" />
      ) : (
        <Loader2 size={12} className="shrink-0 text-text-tertiary" />
      )}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-text-tertiary">
          {label}
        </div>
        <code className="block truncate text-text-primary">{value}</code>
      </div>
    </div>
  );
}

function E2eProbe({
  projectId,
  hostname,
  verifyEpoch,
}: {
  projectId: number;
  hostname: string;
  verifyEpoch: number;
}) {
  const [state, setState] = useState<E2eState>("idle");
  const [detail, setDetail] = useState<string>("");
  const [hint, setHint] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setState("checking");
    Promise.all([probeE2E(hostname), probeServerE2e(projectId)]).then(
      ([browser, server]) => {
        if (cancelled) return;
        const verdict = resolveCustomHostnameE2eVerdict(server, browser);
        setState(verdict.state);
        setDetail(verdict.detail);
        setHint(verdict.hint);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [projectId, hostname, verifyEpoch]);

  return (
    <div
      className={`mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
        state === "ok"
          ? "border-success bg-success-bg/40"
          : state === "fail"
            ? "border-warning bg-warning-bg/40"
            : "border-border-primary bg-bg-tertiary"
      }`}
    >
      {state === "checking" && (
        <Loader2 size={12} className="mt-0.5 shrink-0 animate-spin text-text-tertiary" />
      )}
      {state === "ok" && <Check size={12} className="mt-0.5 shrink-0 text-success" />}
      {state === "fail" && <XIcon size={12} className="mt-0.5 shrink-0 text-warning" />}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-text-primary">
          End-to-End Check{" "}
          <code className="font-normal text-text-tertiary">
            GET https://{hostname}/health
          </code>
        </p>
        <p className="mt-0.5 text-text-secondary">
          {state === "checking" && "Browser → CF → Worker → Railway → API …"}
          {state === "ok" &&
            `Komplette Kette antwortet (${detail}). Tracker-JS würde an dieser Domain ausgeliefert.`}
          {state === "fail" &&
            `Kette unterbrochen: ${detail}. Mögliche Ursachen: DNS noch nicht propagiert, Worker-Route fehlt, oder SSL-Cert nicht ausgestellt.`}
          {state === "idle" && "Wartet auf Daten…"}
        </p>
        {/* Server sees the chain fine, only the operator's own DNS cache is
            stale (TASK-116, live case tr.leben-mit-ohne.de) — explain instead
            of leaving a green check unexplained next to a browser error. */}
        {state === "ok" && hint && (
          <p className="mt-1 text-text-tertiary">{hint}</p>
        )}
      </div>
    </div>
  );
}
